import {
  Leaf,
  ChefHat,
  Sparkles,
  Globe,
  Flame,
  Sun,
  Snowflake,
  Flower2,
  Fish,
  Salad,
  UtensilsCrossed,
  Coffee,
  Soup,
  Apple,
  Wheat,
  Heart,
  Star,
  Wind,
  Sunset,
  TreePine,
} from "lucide-react";
import type { ReactNode } from "react";

export interface ThemeOption {
  id: string;
  label: string;
  description: string;
  icon: ReactNode;
}

export const FIXED_THEMES: ThemeOption[] = [
  {
    id: "light",
    label: "Express & léger",
    description: "Repas rapides, frais et équilibrés",
    icon: <Leaf className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "comfort",
    label: "Classiques réconfortants",
    description: "Plats mijotés, gratins et saveurs d'antan",
    icon: <ChefHat className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "surprise",
    label: "Surprise-moi",
    description: "Laissez la brigade décider du thème cette semaine",
    icon: <Sparkles className="size-5" strokeWidth={1.5} />,
  },
];

export const ROTATING_THEMES: ThemeOption[] = [
  {
    id: "mediterranean",
    label: "Voyage en Méditerranée",
    description: "Soleil, herbes aromatiques et produits de la mer",
    icon: <Sun className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "asian",
    label: "Voyage en Asie",
    description: "Soja, gingembre, lait de coco et nouilles",
    icon: <Globe className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "italian",
    label: "L'Italie dans l'assiette",
    description: "Pâtes, risotto, tomates et basilic frais",
    icon: <UtensilsCrossed className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "moroccan",
    label: "Saveurs marocaines",
    description: "Tajines, épices chaudes et couscous",
    icon: <Flame className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "japanese",
    label: "Inspirations japonaises",
    description: "Umami, dashi, miso et légèreté",
    icon: <Fish className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "indian",
    label: "Épices indiennes",
    description: "Currys, dal, riz basmati et coriandre",
    icon: <Flame className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "mexican",
    label: "Mexique en fête",
    description: "Haricots, avocat, maïs et piment doux",
    icon: <Sun className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "bistrot",
    label: "Bistrot français",
    description: "Tradition, moutarde, vin blanc et beurre",
    icon: <Coffee className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "provencal",
    label: "Provence & herbes",
    description: "Thym, lavande, tomates et huile d'olive",
    icon: <Flower2 className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "nordic",
    label: "Minimalisme nordique",
    description: "Céréales, racines, poisson fumé et aneth",
    icon: <Wind className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "world_cuisine",
    label: "Cuisine du monde",
    description: "Un tour du globe en sept dîners",
    icon: <Globe className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "street_food",
    label: "Street food du monde",
    description: "Wraps, bols, sandwichs et sauces maison",
    icon: <UtensilsCrossed className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "anti_inflammatory",
    label: "Anti-inflammatoire",
    description: "Curcuma, baies, omega-3 et légumes colorés",
    icon: <Heart className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "low_carb",
    label: "Low-carb & protéiné",
    description: "Moins de féculents, plus de protéines et légumes",
    icon: <Salad className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "veggie",
    label: "Tout végétal",
    description: "Légumineuses, céréales et légumes à l'honneur",
    icon: <Apple className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "autumn",
    label: "Saveurs d'automne",
    description: "Courges, champignons, châtaignes et épices douces",
    icon: <TreePine className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "winter_comfort",
    label: "Comfort hivernal",
    description: "Soupes, ragoûts, fromages fondus et racines",
    icon: <Snowflake className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "summer",
    label: "Fraîcheur estivale",
    description: "Salades, grillades, tomates et herbes fraîches",
    icon: <Sunset className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "spring",
    label: "Légèreté printanière",
    description: "Asperges, petits pois, radis et ciboulette",
    icon: <Flower2 className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "homemade",
    label: "Fait maison & généreux",
    description: "Recettes transmises, généreuses et sans chichi",
    icon: <Wheat className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "umami",
    label: "Umami & profondeur",
    description: "Champignons, parmesan, anchois et bouillons riches",
    icon: <Soup className="size-5" strokeWidth={1.5} />,
  },
  {
    id: "rainbow",
    label: "Arc-en-ciel dans l'assiette",
    description: "Le plus de couleurs possibles chaque soir",
    icon: <Star className="size-5" strokeWidth={1.5} />,
  },
];

export const ALL_THEMES: ThemeOption[] = [...FIXED_THEMES, ...ROTATING_THEMES];

export function getThemeById(id: string): ThemeOption | undefined {
  return ALL_THEMES.find((t) => t.id === id);
}

export function getRotatingTheme(): ThemeOption {
  return ROTATING_THEMES[Math.floor(Math.random() * ROTATING_THEMES.length)];
}
